import { useState, useEffect, useMemo } from 'react'
import { useAtom } from 'jotai'
import {
  Button,
  TextField,
  Box,
  CircularProgress,
  Autocomplete,
  Alert,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { Person as PersonIcon, People as PeopleIcon } from '@mui/icons-material'
import { Client, clientsApi, jobOpportunitiesApi, JobOpportunity } from '../../services/api'
import DialogShell from '../common/DialogShell'
import FormSection, { FieldPair } from '../common/FormSection'
import { userAtom } from '../../state/auth'
import { isAdminOrManager } from '../../utils/roles'
import { todayLocalYMD } from '../../utils/dateFormat'
import { jobDateError } from '../../utils/dateRange'
import { isValidJobUrl, ensureHttpProtocol } from '../../utils/validation'

import { ADD_STATUS_OPTIONS, showsAppliedDate } from './addStatusOptions'

interface AddJobDialogProps {
  open: boolean
  onClose: () => void
  // MWA-252 — when the page already has a client selected (header switcher), the
  // dialog locks to that client instead of showing its own picker.
  initialClient?: Client | null
  // MWA-297 — opened from a contact who referred this job. A referred job has
  // been heard about, not applied to, so the applied date must not be
  // pre-filled: recording today would claim a step that never happened.
  referredByName?: string
  onAdd: (data: {
    clientId: string
    clientName?: string
    company: string
    jobTitle: string
    status?: string
    applicationDate?: string
    datePosted?: string
    notes?: string
    url?: string
    location?: string
  }) => Promise<void>
}

export default function AddJobDialog({ open, onClose, onAdd, initialClient = null, referredByName }: AddJobDialogProps) {
  // MWA-297 — a referred job has not been applied to, so there is no applied
  // date to claim. Shared by the initial state and the post-submit reset so the
  // two cannot drift.
  const defaultAppliedDate = () => (referredByName ? '' : todayLocalYMD())
  const [clients, setClients] = useState<Client[]>([])
  const [loadingClients, setLoadingClients] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [selectedClient, setSelectedClient] = useState<Client | null>(null)
  const [duplicateWarning, setDuplicateWarning] = useState(false)
  // MWA-256 — "Not Interested" is per-opportunity (MWA-220): a job the client
  // declined is archived with applyDecision === 'not_interested'. There is no
  // company entity, so we detect a previously-declined company by matching the
  // company name against the client's existing archived/declined opportunities.
  // When matches exist, staff must explicitly Reopen them or acknowledge an
  // "Add anyway" override before we let the create through.
  const [notInterestedMatches, setNotInterestedMatches] = useState<JobOpportunity[]>([])
  const [overrideNotInterested, setOverrideNotInterested] = useState(false)
  const [reopening, setReopening] = useState(false)
  const [notInterestedError, setNotInterestedError] = useState<string | null>(null)
  const [formData, setFormData] = useState({
    company: '',
    jobTitle: '',
    status: '',
    applicationDate: defaultAppliedDate(),
    // No default: unlike the applied date, nobody can infer when a posting went
    // up, and a wrong date here is worse than an absent one.
    datePosted: '',
    url: '',
    notes: '',
    location: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  // #200 — Surface load failures in the dialog. Previously errors were
  // swallowed by `catch { console.error }` which meant a malformed row in the
  // clients list (e.g. one row with `name === undefined`) would silently
  // empty the dropdown with no signal to the user that anything had gone
  // wrong. When this is non-null, render an Alert above the picker.
  const [loadError, setLoadError] = useState<string | null>(null)
  // MWA-224 — picker is scoped to the rep's assigned clients by default;
  // managers/admin can switch to All. Non-managers are always scoped to theirs.
  const [user] = useAtom(userAtom)
  const isManager = isAdminOrManager(user) || user?.role === 'client_success'
  const [pickerScope, setPickerScope] = useState<'my' | 'all'>('my')

  useEffect(() => {
    if (open) {
      // MWA-252 — lock to the page's selected client when provided; otherwise
      // load the picker's client list.
      setSelectedClient(initialClient ?? null)
      // MWA-297 — the dialog stays mounted, so the useState initializer ran
      // long before anyone chose to open it as a referral. Re-apply the applied
      // date on each open, or a referral inherits today's date from whenever
      // the component first mounted.
      setFormData((prev) => ({ ...prev, applicationDate: defaultAppliedDate() }))
      if (!initialClient) loadClients()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // MWA-224 — the picker only ever offers market-ready clients (AC #5), then
  // narrows to the logged-in rep's assigned clients unless a manager flips to All.
  //
  // Deliberately NOT isJobSearchEligible (state/clients.ts), which also allows
  // 'complete'. That helper answers "can staff READ this client's job search",
  // and a finished engagement still has history worth reading. This is a
  // CREATION path: you do not start a new application for a client whose
  // engagement is over.
  const displayedClients = useMemo(() => {
    const marketReady = clients.filter((c) => c.workflowStage === 'market_ready')
    const scopedToMine = !isManager || pickerScope === 'my'
    if (scopedToMine && user?.email) {
      return marketReady.filter(
        (c) => c.coach === user.email || c.clientSuccess === user.email || c.assignedTo === user.email
      )
    }
    return marketReady
  }, [clients, isManager, pickerScope, user?.email])

  // Keep the selection valid: if a scope change removes the chosen client from
  // the visible options, clear it so the Autocomplete value never dangles.
  // Skipped in locked mode (initialClient provided) — there's no picker then.
  useEffect(() => {
    if (initialClient) return
    if (selectedClient && !displayedClients.some((c) => c.id === selectedClient.id)) {
      setSelectedClient(null)
    }
  }, [displayedClients, selectedClient, initialClient])

  const loadClients = async () => {
    setLoadingClients(true)
    setLoadError(null)
    try {
      const result = await clientsApi.list({ all: true })
      // #200 — Defensive sort. A client row missing `name` (undefined or
      // null) would previously throw "Cannot read properties of undefined
      // (reading 'localeCompare')" inside the comparator, abort the entire
      // setClients call, and leave the dropdown empty for everyone. Fall
      // back through email → empty string so the sort still completes.
      // Filter to entries that at least have an id so the Autocomplete
      // option equality check (option.id === value.id) doesn't crash later.
      const safe = (result || []).filter((c) => c && c.id)
      safe.sort((a, b) => {
        const an = a.name || a.email || ''
        const bn = b.name || b.email || ''
        return an.localeCompare(bn)
      })
      setClients(safe)
    } catch (error) {
      console.error('Failed to load clients:', error)
      // Surface to the user. If we ever regress (auth issue, malformed
      // row, network failure, etc.) the user gets an actionable error
      // instead of a silent empty dropdown.
      const msg = error instanceof Error ? error.message : 'Unknown error'
      setLoadError(`Couldn't load the client list (${msg}). Try refreshing — if it persists, ping a coach.`)
    } finally {
      setLoadingClients(false)
    }
  }

  // Check for duplicates (exact company + title) AND prior "Not Interested"
  // matches (company-only) when company or jobTitle changes. Both derive from
  // the same client opportunity list, so we fetch once. MWA-256.
  useEffect(() => {
    const checkExisting = async () => {
      const company = formData.company.trim().toLowerCase()
      if (!selectedClient || !company) {
        setDuplicateWarning(false)
        setNotInterestedMatches([])
        return
      }
      try {
        const existing = await jobOpportunitiesApi.list({ clientId: selectedClient.id })

        // Exact duplicate (company + title) — only meaningful once a title is
        // entered. Non-blocking; unchanged behavior.
        const jobTitle = formData.jobTitle.trim().toLowerCase()
        const hasDup = !!jobTitle && existing.some(
          (j) => j.company.toLowerCase() === company && j.jobTitle.toLowerCase() === jobTitle
        )
        setDuplicateWarning(hasDup)

        // MWA-256 — same company the client previously declined. "Not Interested"
        // (MWA-220) always archives the opportunity, so matching on `archived` is
        // exactly the declined set — and it guarantees the server-side restore
        // guard (which requires existing.archived) succeeds on Reopen. The list
        // already excludes soft-deleted rows server-side.
        const matches = existing.filter(
          (j) => j.company.toLowerCase() === company && j.archived === true
        )
        setNotInterestedMatches(matches)
      } catch {
        // Ignore lookup failures — never block on a failed check.
      }
    }

    const timeout = setTimeout(checkExisting, 500)
    return () => clearTimeout(timeout)
  }, [selectedClient, formData.company, formData.jobTitle])

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }))
    }
    // MWA-256 — an "Add anyway" acknowledgement is scoped to the company that
    // was showing the warning. Editing the company invalidates it so the guard
    // re-evaluates against the new value.
    if (field === 'company') {
      setOverrideNotInterested(false)
      setNotInterestedError(null)
    }
  }

  // MWA-256 — true when the client previously declined this company and the
  // staff member hasn't yet resolved it (neither reopened nor acknowledged).
  // Drives both the submit gate and the disabled "Add Application" button.
  const notInterestedBlocking = notInterestedMatches.length > 0 && !overrideNotInterested

  // MWA-256 — "Reopen {company}": clear the not-interested state on every
  // matching archived opportunity ({ id, restore: true } reopens it server-side,
  // clearing archived + applyDecision). On success the matches list is emptied,
  // which removes the warning and unblocks submission.
  const handleReopen = async () => {
    if (notInterestedMatches.length === 0) return
    setReopening(true)
    setNotInterestedError(null)
    try {
      for (const match of notInterestedMatches) {
        await jobOpportunitiesApi.update({ id: match.id, restore: true })
      }
      setNotInterestedMatches([])
      setOverrideNotInterested(false)
    } catch (error) {
      console.error('Failed to reopen not-interested opportunity:', error)
      setNotInterestedError(
        "Couldn't reopen the previous opportunity. Try again, or use “Add anyway” to proceed without reopening."
      )
    } finally {
      setReopening(false)
    }
  }

  const validate = () => {
    const newErrors: Record<string, string> = {}
    if (!selectedClient) newErrors.clientId = 'Client is required'
    if (!formData.company.trim()) newErrors.company = 'Company is required'
    if (!formData.jobTitle.trim()) newErrors.jobTitle = 'Job title is required'
    if (!isValidJobUrl(formData.url.trim())) {
      newErrors.url = 'Please enter a valid URL'
    }
    const applicationDateError = showsAppliedDate(formData.status) ? jobDateError(formData.applicationDate) : null
    if (applicationDateError) newErrors.applicationDate = applicationDateError
    const datePostedError = jobDateError(formData.datePosted)
    if (datePostedError) newErrors.datePosted = datePostedError
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return
    // MWA-256 — never silently create over a "Not Interested" company. The
    // warning Alert is already rendered; the disabled button also reflects this,
    // but guard here too in case submit is reached another way.
    if (notInterestedBlocking) return

    setSubmitting(true)
    try {
      await onAdd({
        clientId: selectedClient!.id,
        clientName: selectedClient!.name,
        company: formData.company.trim(),
        jobTitle: formData.jobTitle.trim(),
        status: formData.status,
        // MWA-392 — a job pending the client's decision has had nothing submitted,
        // so it carries no applied date whatever the field held.
        applicationDate: showsAppliedDate(formData.status) ? formData.applicationDate || undefined : undefined,
        datePosted: formData.datePosted || undefined,
        url: formData.url.trim() ? ensureHttpProtocol(formData.url.trim()) : undefined,
        notes: formData.notes.trim() || undefined,
        location: formData.location.trim() || undefined,
      })
      handleClose()
    } catch (error) {
      console.error('Failed to create job application:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    setSelectedClient(null)
    setFormData({
      company: '',
      jobTitle: '',
      status: '',
      applicationDate: defaultAppliedDate(),
      datePosted: '',
      url: '',
      notes: '',
      location: '',
    })
    setErrors({})
    setDuplicateWarning(false)
    // MWA-256 — clear the not-interested guard state too.
    setNotInterestedMatches([])
    setOverrideNotInterested(false)
    setReopening(false)
    setNotInterestedError(null)
    onClose()
  }

  // The one message that must stay in view: a blocking not-interested match, or
  // failing that the non-blocking duplicate notice. Docked under the header so
  // it is visible whatever the form is scrolled to, next to the disabled button
  // it explains.
  const dock =
    notInterestedMatches.length > 0 ? (
      // MWA-256 — distinct, blocking warning when this client previously
      // marked this company "Not Interested". Staff must either Reopen the
      // prior opportunity (clears Not Interested) or acknowledge "Add
      // anyway" before the create is allowed.
      <Alert
        severity="warning"
        variant="outlined"
        sx={{
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          '& .MuiAlert-action': { ml: { xs: 0, sm: 'auto' }, pl: { xs: 4.5, sm: 2 } },
        }}
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              sx={{ whiteSpace: 'nowrap' }}
              onClick={handleReopen}
              disabled={reopening || overrideNotInterested}
            >
              {reopening ? 'Reopening...' : `Reopen ${formData.company.trim()}`}
            </Button>
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              sx={{ whiteSpace: 'nowrap' }}
              onClick={() => {
                setOverrideNotInterested(true)
                setNotInterestedError(null)
              }}
              disabled={reopening || overrideNotInterested}
            >
              Add anyway
            </Button>
          </Box>
        }
      >
        {selectedClient?.name || 'This client'} previously marked {formData.company.trim()} as Not Interested.
        {overrideNotInterested && ' Adding anyway.'}
        {notInterestedError && <Box sx={{ mt: 0.5 }}>{notInterestedError}</Box>}
      </Alert>
    ) : duplicateWarning ? (
      <Alert severity="warning" variant="outlined">
        A similar application ({formData.company} — {formData.jobTitle}) already exists for this client. You can still
        create another one.
      </Alert>
    ) : undefined

  return (
    <DialogShell
      open={open}
      onClose={handleClose}
      width={760}
      title={referredByName ? `Add a job ${referredByName} referred` : 'Add Job Application'}
      subtitle={
        initialClient ? (
          <>
            Client: <strong>{initialClient.name || initialClient.email || '(unnamed client)'}</strong>
          </>
        ) : undefined
      }
      dock={dock}
      footer={
        <>
          <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || notInterestedBlocking}
          >
            {submitting ? 'Adding...' : 'Add Application'}
          </Button>
        </>
      }
    >
      {loadError && (
        <Alert severity="error" variant="outlined" onClose={() => setLoadError(null)} sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      <FormSection title="The role" first>
        {!initialClient && (
          <>
            {isManager && (
              <ToggleButtonGroup
                value={pickerScope}
                exclusive
                size="small"
                onChange={(_, v) => v && setPickerScope(v)}
                sx={{ alignSelf: 'flex-start' }}
              >
                <ToggleButton value="my">
                  <PersonIcon sx={{ mr: 0.5 }} fontSize="small" />My Clients
                </ToggleButton>
                <ToggleButton value="all">
                  <PeopleIcon sx={{ mr: 0.5 }} fontSize="small" />All
                </ToggleButton>
              </ToggleButtonGroup>
            )}
            <Autocomplete
              options={displayedClients}
              // #200 — Tolerate missing names. If a client row is missing
              // `name` we fall back to email, then to an "Unnamed client" hint
              // so the row is still selectable rather than rendering "undefined".
              getOptionLabel={(option) => {
                const name = option.name || '(unnamed client)'
                const email = option.email ? ` (${option.email})` : ''
                return `${name}${email}`
              }}
              value={selectedClient}
              onChange={(_, newValue) => {
                setSelectedClient(newValue)
                if (errors.clientId) setErrors((prev) => ({ ...prev, clientId: '' }))
                // MWA-256 — a not-interested acknowledgement is per client+company;
                // switching client invalidates it.
                setOverrideNotInterested(false)
                setNotInterestedError(null)
              }}
              loading={loadingClients}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client"
                  required
                  error={!!errors.clientId}
                  helperText={errors.clientId}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingClients ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText={loadingClients ? 'Loading...' : 'No market-ready clients'}
              fullWidth
            />
          </>
        )}

        <FieldPair>
          <TextField
            label="Company"
            value={formData.company}
            onChange={(e) => handleChange('company', e.target.value)}
            error={!!errors.company}
            helperText={errors.company}
            fullWidth
            required
          />
          <TextField
            label="Job Title"
            value={formData.jobTitle}
            onChange={(e) => handleChange('jobTitle', e.target.value)}
            error={!!errors.jobTitle}
            helperText={errors.jobTitle}
            fullWidth
            required
          />
        </FieldPair>
      </FormSection>

      <FormSection title="Where it stands">
        {/* MWA-251 — status at creation. Default "Pending Client Decision" sources a decision;
            later statuses log an already-progressed application directly. */}
        <FormControl fullWidth>
          {/* shrink is the counterpart of displayEmpty below: the Select always renders
              content, so the label must always be raised or it sits on top of it. */}
          <InputLabel shrink>Status</InputLabel>
          <Select
            value={formData.status}
            label="Status"
            // The default option carries the empty value, which is what
            // awaiting the client's decision means. Without displayEmpty a MUI
            // Select holding '' renders blank, so the field showed nothing at
            // all where it should read "Pending Client Decision".
            displayEmpty
            onChange={(e) => handleChange('status', e.target.value)}
          >
            {ADD_STATUS_OPTIONS.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            Pending Client Decision sends no status — it waits for the client, and the applied date is set when the job moves to Applied.
          </FormHelperText>
        </FormControl>
      </FormSection>

      <FormSection title="Dates and place">
        <FieldPair>
          {/* MWA-392 — only once an application has been submitted. */}
          {showsAppliedDate(formData.status) && (
            <TextField
              label="Date Applied"
              type="date"
              value={formData.applicationDate}
              onChange={(e) => handleChange('applicationDate', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              error={!!errors.applicationDate}
              helperText={errors.applicationDate}
            />
          )}
          {/* Optional and undefaulted: nobody can infer when a posting went up,
              and a wrong date here is worse than an absent one. */}
          <TextField
            label="Date Posted"
            type="date"
            value={formData.datePosted}
            onChange={(e) => handleChange('datePosted', e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            error={!!errors.datePosted}
            helperText={errors.datePosted || 'When the job was advertised (optional)'}
          />
        </FieldPair>
        <TextField
          label="Location"
          value={formData.location}
          onChange={(e) => handleChange('location', e.target.value)}
          fullWidth
        />
      </FormSection>

      <FormSection title="Reference">
        <TextField
          label="Job Posting URL"
          value={formData.url}
          onChange={(e) => handleChange('url', e.target.value)}
          fullWidth
          placeholder="https://..."
          error={!!errors.url}
          helperText={errors.url}
        />
        <TextField
          label="Notes"
          value={formData.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          multiline
          rows={3}
          fullWidth
        />
      </FormSection>
    </DialogShell>
  )
}
